import React from 'react';
import moment from 'moment-timezone';
import { getProjectColorFromId, getNoProjectColor } from 'utils/time_tracker_project_color';

const TASK_SUGGEST_PREFIX = '__tt_sug:v1:';
const SCHEDULE_SUGGEST_PREFIX = '__tt_sched:v1:';

/**
 * Recent unique (task_description, project) pairs from the user's entries.
 * @param {object[]} entries
 * @param {object|null} runningEntry
 * @param {number} max
 */
export function buildTaskDescriptionSuggestions(entries, runningEntry, max = 80) {
  const byKey = new Map();
  const visit = (e) => {
    if (!e) return;
    const desc = String(e.task_description ?? '').trim();
    if (!desc) return;
    const pidRaw = e.project?._id ?? e.project;
    const projectId =
      pidRaw != null && pidRaw !== '' ? String(pidRaw) : '';
    const key = `${projectId}\0${desc.toLowerCase()}`;
    const t = new Date(
      e.created_at || e.createdAt || e.stopped_at || e.started_at || Date.now(),
    ).getTime();
    const prev = byKey.get(key);
    const projectName = e.project?.project_name ?? null;
    if (!prev || t > prev.lastUsed) {
      byKey.set(key, {
        taskDescription: desc,
        projectId: projectId || null,
        projectName,
        lastUsed: t,
      });
    }
  };
  (entries || []).forEach(visit);
  if (runningEntry) visit(runningEntry);
  return Array.from(byKey.values())
    .sort((a, b) => b.lastUsed - a.lastUsed)
    .slice(0, max);
}

export function encodeTaskSuggestOptionValue(task, projectId) {
  return `${TASK_SUGGEST_PREFIX}${JSON.stringify({
    task,
    projectId: projectId || '',
  })}`;
}

/** @returns {{ task: string, projectId: string|null } | null} */
export function parseTaskSuggestOptionValue(val) {
  if (typeof val !== 'string' || !val.startsWith(TASK_SUGGEST_PREFIX)) {
    return null;
  }
  try {
    const payload = JSON.parse(val.slice(TASK_SUGGEST_PREFIX.length));
    if (!payload || typeof payload.task !== 'string') return null;
    return {
      task: payload.task,
      projectId: payload.projectId ? String(payload.projectId) : null,
    };
  } catch {
    return null;
  }
}

export function encodeScheduleSuggestOptionValue(shiftId, task) {
  return `${SCHEDULE_SUGGEST_PREFIX}${JSON.stringify({
    shiftId: String(shiftId),
    task: task || '',
  })}`;
}

/** @returns {{ shiftId: string, task: string } | null} */
export function parseScheduleSuggestOptionValue(val) {
  if (typeof val !== 'string' || !val.startsWith(SCHEDULE_SUGGEST_PREFIX)) {
    return null;
  }
  try {
    const payload = JSON.parse(val.slice(SCHEDULE_SUGGEST_PREFIX.length));
    if (!payload || !payload.shiftId) return null;
    return {
      shiftId: String(payload.shiftId),
      task: typeof payload.task === 'string' ? payload.task : '',
    };
  } catch {
    return null;
  }
}

/**
 * Put today's scheduled shifts at the top of the task dropdown.
 * Activity name acts as the task (same idea as Time Tracker task text).
 * @param {object[]} shifts
 * @param {string} tz
 */
export function buildScheduleSuggestSelectOptions(shifts, tz) {
  return (shifts || []).map((shift) => {
    const activityName = shift.activity?.name || 'Scheduled shift';
    const start = moment.tz(shift.starts_at, tz).format('h:mm A');
    const end = moment.tz(shift.ends_at, tz).format('h:mm A');
    const color = shift.color || shift.activity?.color || '#1890ff';
    return {
      value: encodeScheduleSuggestOptionValue(shift._id, activityName),
      taskFilter: `${activityName} ${start} ${end}`,
      label: React.createElement(
        'div',
        { className: 'clockify-task-suggest-option clockify-task-suggest-option--schedule' },
        React.createElement(
          'span',
          { className: 'clockify-task-suggest-text' },
          activityName,
        ),
        React.createElement(
          'span',
          { className: 'clockify-task-suggest-project' },
          React.createElement('span', {
            className: 'clockify-task-suggest-project-dot',
            style: {
              display: 'inline-block',
              width: 8,
              height: 8,
              borderRadius: '50%',
              marginRight: 6,
              verticalAlign: 'middle',
              background: color,
            },
          }),
          `Scheduled · ${start} – ${end}`,
        ),
      ),
      key: `sched-${shift._id}`,
    };
  });
}

/**
 * @param {{ taskDescription: string, projectId: string|null, projectName: string|null }[]} suggestions
 * @param {Map<string, { project_name?: string, color?: string, _id?: string }>} projectsById
 */
export function buildTaskSuggestSelectOptions(suggestions, projectsById) {
  return suggestions.map((s, i) => {
    const pname =
      s.projectName ||
      (s.projectId && projectsById.get(s.projectId)?.project_name) ||
      (s.projectId ? 'Project' : 'No project');
    const dotColor = s.projectId
      ? getProjectColorFromId(s.projectId, projectsById)
      : getNoProjectColor();
    return {
      value: encodeTaskSuggestOptionValue(s.taskDescription, s.projectId),
      taskFilter: s.taskDescription,
      label: React.createElement(
        'div',
        { className: 'clockify-task-suggest-option' },
        React.createElement(
          'span',
          { className: 'clockify-task-suggest-text' },
          s.taskDescription,
        ),
        React.createElement(
          'span',
          { className: 'clockify-task-suggest-project' },
          React.createElement('span', {
            className: 'clockify-task-suggest-project-dot',
            style: {
              display: 'inline-block',
              width: 8,
              height: 8,
              borderRadius: '50%',
              marginRight: 6,
              verticalAlign: 'middle',
              background: dotColor,
            },
          }),
          pname,
        ),
      ),
      key: `${s.projectId || 'none'}-${i}-${String(s.taskDescription).slice(0, 20)}`,
    };
  });
}

export function filterTaskSuggestOption(input, option) {
  const text = String(
    option?.taskFilter ?? option?.props?.taskFilter ?? '',
  );
  return text.toLowerCase().includes(String(input ?? '').toLowerCase().trim());
}
